'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { SOCKET_EVENTS } from '@/lib/constants/socket';
import { logger } from '@/lib/monitoring/logger';
import type {
  AuctionRoom,
  Bid,
  SocketResponse,
  Team,
  Player,
} from '@/lib/types/auction';
import { useSocket } from './useSocket';
import toast from 'react-hot-toast';

interface UseAuctionProps {
  tournamentId: string;
  team?: Team;
  playerId?: string;
  tournament?: { status: string }; // Add tournament status check
}

export function useAuction({
  tournamentId,
  team,
  playerId,
  tournament,
}: UseAuctionProps) {
  const { socket, subscribe, unsubscribe } = useSocket();
  const [room, setRoom] = useState<AuctionRoom | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [playerStatusInfo, setPlayerStatusInfo] = useState<{
    player: Player;
    team?: Team;
    amount?: number;
    status: 'sold' | 'unsold';
  } | null>(null);

  const mountedRef = useRef(true);
  const lastBidTime = useRef<number>(0);
  const bidQueue = useRef<Bid[]>([]);
  const batchUpdateTimeout = useRef<NodeJS.Timeout | null>(null);
  const currentPlayerId = useRef<string | undefined>(playerId);

  /** ✅ Process Bid Queue in Batches */
  const processBidQueue = useCallback(() => {
    if (bidQueue.current.length > 0 && mountedRef.current) {
      setBids((prev) => [...bidQueue.current.reverse(), ...prev]);
      bidQueue.current = [];
    }
  }, []);

  /** ✅ Handle Placing a Bid */
  const handleBid = async (amount: number) => {
    try {
      if (!room?.currentPlayer) throw new Error('No active player');
      if (!team) throw new Error('You are not allowed to bid for player');
      if (!socket) throw new Error('Socket not connected');

      // Throttle protection - 500ms minimum between bids
      const now = Date.now();
      if (now - lastBidTime.current < 500)
        throw new Error('Please wait before placing another bid');

      lastBidTime.current = now;

      logger.info('Placing bid', {
        amount,
        teamId: team.id,
        playerId: currentPlayerId.current || room.currentPlayer.id,
      });

      return new Promise((resolve, reject) => {
        socket.emit(
          SOCKET_EVENTS.PLACE_BID,
          {
            amount,
            team,
            tournamentId,
            playerId: currentPlayerId.current || room.currentPlayer?.id,
            timestamp: new Date(),
          },
          (response: SocketResponse) => {
            if (response?.status === 0) {
              reject(
                new Error(response.error?.errorMessage || 'Failed to place bid')
              );
            } else {
              resolve(response);
            }
          }
        );
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to place bid';
      setError(message);
      toast.error(message);
      throw err; // Re-throw to be handled by BiddingPanel
    }
  };

  const handleSkipPlayer = useCallback(() => {
    if (!room?.currentPlayer) return;

    // Show unsold notification immediately
    setPlayerStatusInfo({
      player: room.currentPlayer,
      status: 'unsold',
    });

    // Keep current room state during notification
    setRoom((prev) =>
      prev
        ? {
            ...prev,
            status: 'waiting',
            currentBid: null,
          }
        : null
    );

    // Emit skip event to backend
    socket?.emit(SOCKET_EVENTS.SKIP_PLAYER, {
      playerId: room.currentPlayer.id,
      tournamentId,
    });
  }, [room?.currentPlayer, socket, tournamentId]);

  useEffect(() => {
    mountedRef.current = true;

    // Don't attempt any socket operations if tournament is not active
    if (!tournamentId || tournament?.status !== 'ACTIVE') {
      setIsConnected(false);
      setRoom(null);
      setError(null); // Clear any existing errors
      return;
    }

    let reconnectMessageTimeout: NodeJS.Timeout;

    if (!socket) {
      // Only show reconnect message for ACTIVE tournaments
      reconnectMessageTimeout = setTimeout(() => {
        if (!socket && mountedRef.current && tournament?.status === 'ACTIVE') {
          toast.error('Socket is not available. Attempting to reconnect...');
        }
      }, 1000);
      return () => {
        clearTimeout(reconnectMessageTimeout);
      };
    }

    /** ✅ Socket Event Handlers */
    const handleConnect = () => {
      if (!mountedRef.current) return;
      clearTimeout(reconnectMessageTimeout);
      logger.info('Socket connected, in auction context', { tournamentId });
      setIsConnected(true);
      setError(null);

      // Request auction status with playerId if available
      if (currentPlayerId.current) {
        socket.emit(SOCKET_EVENTS.AUCTION_STATUS, {
          tournamentId,
          playerId: currentPlayerId.current,
        });
      } else {
        socket.emit(SOCKET_EVENTS.AUCTION_STATUS, { tournamentId });
      }
    };

    const handleDisconnect = () => {
      if (!mountedRef.current) return;
      logger.warn('Socket disconnected');
      toast.error(
        'Disconnected from the system. Please refresh once to reconnect'
      );
    };

    const handleSocketError = (data: SocketResponse<{ message: string }>) => {
      if (!mountedRef.current || data.status !== 0) return;
      const errorMessage = data.error.errorMessage || 'Unknown socket error';
      logger.error('Socket error:', new Error(errorMessage));
      setError(errorMessage);
    };

    const handleAuctionStatus = (data: SocketResponse<AuctionRoom>) => {
      if (!mountedRef.current || data.status !== 1) return;
      logger.info('Received auction status', {
        status: data.result.status,
        hasPlayer: !!data.result.currentPlayer,
        tournamentId,
      });

      // Only update room if there's no active notification
      if (!playerStatusInfo) {
        if (data.result.currentPlayer && data.result.currentPlayer.id) {
          currentPlayerId.current = data.result.currentPlayer.id;
        }

        setRoom(data.result);

        if (data.result?.bids && data.result.bids.length > 0) {
          setBids(data.result.bids);
        }

        setError(null);
      }
    };

    const handleBidPlaced = (data: SocketResponse<AuctionRoom>) => {
      if (!mountedRef.current || data.status !== 1) {
        return;
      }

      logger.info('BID_PLACED event received', {
        hasPlayer: !!data.result.currentPlayer,
        hasBids: !!data.result.bids,
        bidsCount: data.result.bids?.length || 0,
      });

      // Store the current player ID for future requests
      if (data.result.currentPlayer && data.result.currentPlayer.id) {
        currentPlayerId.current = data.result.currentPlayer.id;
      }

      setRoom(data.result);

      // Process new bids if they exist
      if (data.result?.bids && data.result.bids.length > 0) {
        // Get the latest bid (first in the array)
        const latestBid = data.result.bids[0];

        // Add to queue for batch processing
        if (latestBid) {
          bidQueue.current.push(latestBid);
        }

        // Update all bids for initial state
        setBids(data.result.bids);
      }

      // Clear existing timeout & set a new one for batch update
      if (batchUpdateTimeout.current) clearTimeout(batchUpdateTimeout.current);
      batchUpdateTimeout.current = setTimeout(processBidQueue, 100);

      setError(null);
    };

    const handlePlayerSold = (
      data: SocketResponse<{
        player: Player;
        team: Team;
        amount: number;
        timestamp: string;
      }>
    ) => {
      if (!mountedRef.current || data.status !== 1) return;
      logger.info('PLAYER_SOLD event received', {
        playerId: data.result.player.id,
        teamId: data.result.team.id,
        amount: data.result.amount,
      });

      setBids([]);
      setPlayerStatusInfo({
        player: data.result.player,
        team: data.result.team,
        amount: data.result.amount,
        status: 'sold',
      });

      socket.emit(SOCKET_EVENTS.AUCTION_STATUS, { tournamentId });
    };

    const handlePlayerUnsold = (
      data: SocketResponse<{
        player: Player;
        status: string;
        timestamp: string;
      }>
    ) => {
      if (!mountedRef.current || data.status !== 1) return;
      logger.info('PLAYER_UNSOLD event received', {
        playerId: data.result.player.id,
        status: data.result.status,
      });

      // Set player status info to show notification
      setPlayerStatusInfo({
        player: data.result.player,
        status: 'unsold',
      });

      // Keep the current room state during notification
      setRoom((prev) =>
        prev
          ? {
              ...prev,
              status: 'waiting',
              currentBid: null,
            }
          : null
      );
    };

    const handlePoolCompleted = (
      data: SocketResponse<{
        poolId: string;
        poolName: string;
      }>
    ) => {
      if (!mountedRef.current || data.status !== 1) return;

      logger.info('POOL_COMPLETED event received', {
        poolId: data.result.poolId,
        poolName: data.result.poolName,
      });

      // Clear auction state when pool is completed
      setRoom(null);
      setBids([]);
    };

    /** ✅ Subscribe to Socket Events */
    if (subscribe) {
      subscribe(SOCKET_EVENTS.CONNECT, handleConnect);
      subscribe(SOCKET_EVENTS.DISCONNECT, handleDisconnect);
      subscribe(SOCKET_EVENTS.AUCTION_STATUS, handleAuctionStatus);
      subscribe(SOCKET_EVENTS.BID_PLACED, handleBidPlaced);
      subscribe(SOCKET_EVENTS.PLAYER_SOLD, handlePlayerSold);
      subscribe(SOCKET_EVENTS.PLAYER_UNSOLD, handlePlayerUnsold);
      subscribe(SOCKET_EVENTS.POOL_COMPLETED, handlePoolCompleted);
      subscribe(SOCKET_EVENTS.ERROR, handleSocketError);
    }

    // Set initial connection state and request auction status
    if (socket?.connected) {
      setIsConnected(true);

      // Request auction status with playerId if available
      if (currentPlayerId.current) {
        socket.emit(SOCKET_EVENTS.AUCTION_STATUS, {
          tournamentId,
          playerId: currentPlayerId.current,
        });
      } else {
        socket.emit(SOCKET_EVENTS.AUCTION_STATUS, { tournamentId });
      }
    }

    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectMessageTimeout);
      if (batchUpdateTimeout.current) clearTimeout(batchUpdateTimeout.current);

      /** ✅ Unsubscribe from Socket Events */
      if (unsubscribe) {
        unsubscribe(SOCKET_EVENTS.CONNECT, handleConnect);
        unsubscribe(SOCKET_EVENTS.DISCONNECT, handleDisconnect);
        unsubscribe(SOCKET_EVENTS.AUCTION_STATUS, handleAuctionStatus);
        unsubscribe(SOCKET_EVENTS.BID_PLACED, handleBidPlaced);
        unsubscribe(SOCKET_EVENTS.PLAYER_SOLD, handlePlayerSold);
        unsubscribe(SOCKET_EVENTS.PLAYER_UNSOLD, handlePlayerUnsold);
        unsubscribe(SOCKET_EVENTS.POOL_COMPLETED, handlePoolCompleted);
        unsubscribe(SOCKET_EVENTS.ERROR, handleSocketError);
      }
    };
  }, [
    socket,
    tournamentId,
    tournament?.status,
    playerStatusInfo,
    subscribe,
    unsubscribe,
    processBidQueue,
  ]);

  // Clear player status info after delay
  const clearPlayerStatusInfo = useCallback(() => {
    setPlayerStatusInfo(null);
  }, []);

  return {
    room,
    bids,
    isConnected,
    error,
    placeBid: handleBid,
    playerStatusInfo,
    clearPlayerStatusInfo,
    handleSkipPlayer,
  };
}
